import { Injectable } from '@angular/core';
import {
	FnChartType,
	ChartSettingConfig,
	BNEStackedChartConf,
} from '../../../constant/fn-chart-type.enum';
import { isNonEmptyString, isNotNull } from '../../../util/check';
import { hwAny } from '../../../util/types';

declare var am4core: hwAny;
declare var am4charts: hwAny;
/**
 * Inject this service to set the default perperties of Chart Object.
 */
@Injectable()
export class FnChartService {
	/**
	 * Creates an instance of fn chart service.
	 */
	constructor() {}

	/**
	 * Set Config for different type of Charts
	 */
	private DEFAULT_CATEGORY = 'busiDate';
	public setConfig(
		chart: any,
		chartType: string | number,
		config: ChartSettingConfig
	) {
		switch (chartType) {
			case FnChartType.XYChart:
				chart.paddingRight = 20;
				chart.data = [];

				const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
				dateAxis.renderer.grid.template.location = 0;

				const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
				valueAxis.tooltip.disabled = true;
				valueAxis.renderer.minWidth = 35;

				const series = chart.series.push(new am4charts.LineSeries());
				series.dataFields.dateX = config.category || this.DEFAULT_CATEGORY;
				series.dataFields.valueY = 'value';

				series.tooltipText = '{valueY.value}';
				chart.cursor = new am4charts.XYCursor();

				const scrollbarX = new am4charts.XYChartScrollbar();
				scrollbarX.series.push(series);
				chart.scrollbarX = scrollbarX;

				break;

			case FnChartType.StackColumnChart:
				chart.colors.list = [];
				for (const i of config.stackedValues) {
					chart.colors.list.push(am4core.color(i.color));
				}
				const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
				categoryAxis.dataFields.category =
					config.category || this.DEFAULT_CATEGORY;
				categoryAxis.renderer.grid.template.location = 0;
				categoryAxis.renderer.minGridDistance = 10;

				const sValueAxis = chart.yAxes.push(new am4charts.ValueAxis());
				chart.events.on('ready', (ev: hwAny) => {
					sValueAxis.min = sValueAxis.minZoomed < 0 ? 0 : sValueAxis.minZoomed;
					sValueAxis.max = sValueAxis.maxZoomed < 8 ? 8 : sValueAxis.maxZoomed;
				});

				// Create series
				for (const i of config.stackedValues) {
					this.createStackSeries(chart, i, config.category);
				}

				// Legend
				chart.legend = new am4charts.Legend();
				const markerTemplateS = chart.legend.markers.template;
				chart.colors.step = 4;
				chart.legend.useDefaultMarker = true;
				chart.legend.position = 'bottom';
				chart.legend.contentAlign = config.contentAlign;
				markerTemplateS.width = config.lableWidth;
				markerTemplateS.strokeWidth = 2;
				markerTemplateS.strokeOpacity = 1;
				markerTemplateS.height = 10;

				break;

			case FnChartType.LineChart:
				chart.colors.list = [];
				for (const i of config.stackedValues) {
					chart.colors.list.push(am4core.color(i.color));
				}
				const categoryAxisLine = chart.xAxes.push(new am4charts.CategoryAxis());
				categoryAxisLine.dataFields.category =
					config.category || this.DEFAULT_CATEGORY;
				categoryAxisLine.renderer.grid.template.location = 0;
				categoryAxisLine.renderer.minGridDistance = 10;

				const lValueAxis = chart.yAxes.push(new am4charts.ValueAxis());
				lValueAxis.numberFormatter = new am4core.NumberFormatter();
				lValueAxis.numberFormatter.numberFormat = config.beforeYAxisFormat+"#,###.##";
				chart.events.on('ready', (ev:any) => {
					lValueAxis.min = lValueAxis.minZoomed < 0 ? 0 : lValueAxis.minZoomed;
					lValueAxis.max = lValueAxis.maxZoomed < 8 ? 8 : lValueAxis.maxZoomed;
				});

				// Create series
				for (const i of config.stackedValues) {
					this.createLineSeries(chart, i, config.category,config.bulletStyle);
				}

				// Legend
				chart.legend = new am4charts.Legend();
				const lMarkerTemplate = chart.legend.markers.template;
				chart.colors.step = 4;
				chart.legend.useDefaultMarker = true;
				chart.legend.position = 'bottom';
				chart.legend.contentAlign = config.contentAlign;
				lMarkerTemplate.width = 10;
				lMarkerTemplate.strokeWidth = 2;
				lMarkerTemplate.strokeOpacity = 1;
				lMarkerTemplate.height = 10;

				break;

			case FnChartType.ClusterColumnChart:
				chart.colors.list = [];
				for (const i of config.stackedValues) {
					chart.colors.list.push(am4core.color(i.color));
				}
				const categoryAxisC = chart.xAxes.push(new am4charts.CategoryAxis());
				categoryAxisC.dataFields.category =
					config.category || this.DEFAULT_CATEGORY;
				categoryAxisC.renderer.grid.template.location = 0;
				categoryAxisC.renderer.minGridDistance = 10;
				categoryAxisC.renderer.cellStartLocation = 0.2;
				categoryAxisC.renderer.cellEndLocation = 0.8;

				const cValueAxis = chart.yAxes.push(new am4charts.ValueAxis());
				chart.events.on('ready', (ev: hwAny) => {
					cValueAxis.min = cValueAxis.minZoomed < 0 ? 0 : cValueAxis.minZoomed;
					cValueAxis.max = cValueAxis.maxZoomed < 8 ? 8 : cValueAxis.maxZoomed;
				});
				// Create series
				if (config)
					for (const i of config.stackedValues) {
						this.createClusterSeries(chart, i);
					}

				// Legend
				chart.legend = new am4charts.Legend();
				const markerTemplate = chart.legend.markers.template;
				chart.colors.step = 4;
				chart.legend.useDefaultMarker = true;
				chart.legend.position = 'bottom';
				chart.legend.contentAlign = config.contentAlign;
				markerTemplate.width = config.lableWidth;
				markerTemplate.strokeWidth = 2;
				markerTemplate.strokeOpacity = 1;
				markerTemplate.height = 10;
				break;

			case FnChartType.PieChart:
				chart.data = [];
				chart.legend = new am4charts.Legend();
				const pieSeries = chart.series.push(new am4charts.PieSeries());
				pieSeries.colors.list = [];
				const colorList = config.colorList || [];
				for (let i = 0; i < colorList.length; i++) {
					pieSeries.colors.list.push(this.fnChartColor(colorList[i]));
				}
				pieSeries.dataFields.value = 'value';
				pieSeries.dataFields.category =
					config.category || this.DEFAULT_CATEGORY;
				pieSeries.labels.template.disabled = true;
				pieSeries.ticks.template.disabled = true;
				chart.legend.useDefaultMarker = true;
				chart.legend.position = 'bottom';
				chart.legend.contentAlign = config.contentAlign;
				break;

			case FnChartType.DonutChart:
				chart.data = [];
				chart.innerRadius = am4core.percent(60);
				chart.radius = am4core.percent(90);
				const donutSeries = chart.series.push(new am4charts.PieSeries());
				donutSeries.dataFields.value = 'value';
				donutSeries.dataFields.category =
					config.category || this.DEFAULT_CATEGORY;
				donutSeries.labels.template.disabled = true;
				donutSeries.slices.template.tooltipText = `{category}: {value.percent.formatNumber('#.##')}% ({value})`;
				// This creates initial animation
				donutSeries.hiddenState.properties.opacity = 1;
				donutSeries.hiddenState.properties.endAngle = -90;
				donutSeries.hiddenState.properties.startAngle = -90;
				donutSeries.slices.template.strokeOpacity = 1;
				this.setFnChartConfig(donutSeries, config);
				break;
		}
	}
	private setFnChartConfig(donutSeries: any, config: ChartSettingConfig) {
		if (isNotNull(config)) {
			donutSeries.colors.list = [];
			const colorList = config.colorList || [];
			for (let i = 0; i < colorList.length; i++) {
				donutSeries.colors.list.push(this.fnChartColor(colorList[i]));
			}
			donutSeries.slices.template.strokeWidth = config.strokeWidth;
			if (isNonEmptyString(config.label)) {
				const label = donutSeries.createChild(am4core.Label);
				const color = config.labelColor || '';
				label.text = config.label;
				donutSeries.ticks.template.disabled = true;
				label.horizontalCenter = 'middle';
				label.verticalCenter = 'middle';
				label.fill = am4core.color(color);
				label.fontSize = config.labelFont || 10;
			}
		}
	}
	private fnChartColor(color: string) {
		return am4core.color(color);
	}

	private createStackSeries(
		chart: hwAny,
		chartConf: BNEStackedChartConf,
		category: string = this.DEFAULT_CATEGORY
	) {
		const cSeries = chart.series.push(new am4charts.ColumnSeries());
		const columnTemplate = cSeries.columns.template;
		cSeries.fillColor = am4core.color(chartConf.color);
		cSeries.name = chartConf.title;
		cSeries.dataFields.valueY = chartConf.key;
		cSeries.tooltip.label.textAlign = 'middle';
		cSeries.dataFields.categoryX = category;

		// Make it stacked
		cSeries.stacked = true;

		// Configure columns
		columnTemplate.tooltipText =
			'[bold, font-size:12px]{name}[/]\n[font-size:12px]{categoryX}: [bold]{valueY}';
	}

	private createLineSeries(
		chart:any,
		chartConf: BNEStackedChartConf,
		category: string = this.DEFAULT_CATEGORY,
		bulletStyle:string = ''
	) {
		const lineSeries = chart.series.push(new am4charts.LineSeries());

		lineSeries.name = chartConf.title;
		lineSeries.dataFields.valueY = chartConf.key;
		lineSeries.dataFields.categoryX = category;

		lineSeries.stroke = am4core.color(chartConf.color);
		switch(bulletStyle){
			case 'CIRCLE':
				let circleBullet = lineSeries.bullets.push(new am4charts.CircleBullet());
				circleBullet.circle.stroke = am4core.color(chartConf.color);
				circleBullet.circle.strokeWidth = 1;
				circleBullet.width = 5;
				circleBullet.height = 5;
				circleBullet.tooltipText= '[bold, font-size:12px]{name}[/]\n[font-size:12px]{categoryX}: [bold]{valueY}';
				break;
			case 'RECTANGLE':
				let rectableBullet = lineSeries.bullets.push(new am4charts.rectableBullet());
				rectableBullet.circle.stroke = am4core.color(chartConf.color);
				rectableBullet.circle.strokeWidth = 1;
				rectableBullet.width = 5;
				rectableBullet.height = 5;
				circleBullet.tooltipText= '[bold, font-size:12px]{name}[/]\n[font-size:12px]{categoryX}: [bold]{valueY}';
				break;
		}
		lineSeries.strokeWidth = 3;
		lineSeries.propertyFields.strokeDasharray = 'lineDash';
		lineSeries.tooltip.label.textAlign = 'middle';
	}

	private createClusterSeries(
		chart:any,
		chartConf: BNEStackedChartConf,
		category: string = this.DEFAULT_CATEGORY
	) {
		const cSeries = chart.series.push(new am4charts.ColumnSeries());
		const columnTemplate = cSeries.columns.template;
		cSeries.fillColor = am4core.color(chartConf.color);
		cSeries.name = chartConf.title;
		cSeries.dataFields.valueY = chartConf.key;
		cSeries.dataFields.categoryX = category;
		cSeries.tooltip.label.textAlign = 'middle';
		cSeries.columns.template.width = am4core.percent(80);

		// Make it cluster
		cSeries.stacked = false;

		// Configure columns
		columnTemplate.tooltipText =
			'[bold, font-size:12px]{name}[/]\n[font-size:12px]{categoryX}: [bold]{valueY}';
	}
}
